import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Link } from 'react-router-dom';

export default function BondsPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <span className="text-purple-600">Bonds Investment</span>
            </h1>
            <p className="text-xl text-gray-600">
              Secure fixed income investments for stable returns
            </p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* What are Bonds */}
            <section className="bg-purple-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What are Bonds?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Bonds are fixed-income securities where you lend money to the government or corporations 
                for a fixed period and receive regular interest payments. They offer more stable and 
                predictable returns compared to equities.
              </p>
              <div className="bg-white rounded-xl p-6 border-2 border-purple-200">
                <h3 className="font-bold text-lg mb-3">💎 Key Benefits:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Fixed, predictable interest income</li>
                  <li>• Lower risk compared to stocks</li>
                  <li>• Capital preservation</li>
                  <li>• Diversification for your portfolio</li>
                </ul>
              </div>
            </section>

            {/* Types of Bonds */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Types of Bonds</h2>
              <div className="grid gap-6">
                <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-purple-300 transition-colors">
                  <h3 className="font-bold text-lg mb-2 text-purple-600">Government Bonds</h3>
                  <p className="text-gray-600">Issued by the Government of India. Safest bonds with sovereign guarantee. Suitable for risk-averse investors.</p>
                </div>
                <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-purple-300 transition-colors">
                  <h3 className="font-bold text-lg mb-2 text-purple-600">Corporate Bonds</h3>
                  <p className="text-gray-600">Issued by companies. Higher returns than government bonds but with slightly higher risk. Rated by credit agencies.</p>
                </div>
                <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-purple-300 transition-colors">
                  <h3 className="font-bold text-lg mb-2 text-purple-600">Tax-Free Bonds</h3>
                  <p className="text-gray-600">Interest income is tax-free. Issued by government entities. Ideal for high-income taxpayers.</p>
                </div>
                <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-purple-300 transition-colors">
                  <h3 className="font-bold text-lg mb-2 text-purple-600">RBI Bonds</h3>
                  <p className="text-gray-600">Floating Rate Savings Bonds. Safe investment with returns linked to NSC rates. No lock-in period.</p>
                </div>
              </div>
            </section>

            {/* Key Features */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Why Invest in Bonds?</h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">Regular Income:</strong>
                    <span className="text-gray-700"> Receive fixed interest payments at regular intervals (monthly/quarterly/annual)</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">Capital Safety:</strong>
                    <span className="text-gray-700"> Government bonds backed by sovereign guarantee, corporate bonds rated by agencies</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">Portfolio Diversification:</strong>
                    <span className="text-gray-700"> Reduces overall portfolio risk when combined with equity investments</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <strong className="text-gray-900">Tax Benefits:</strong>
                    <span className="text-gray-700"> Tax-free bonds offer completely tax-free interest income</span>
                  </div>
                </li>
              </ul>
            </section>

            {/* Who Should Invest */}
            <section className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Who Should Invest in Bonds?</h2>
              <ul className="space-y-3">
             
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Conservative investors who prioritize capital safety</span>
                </li>
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Investors wanting to diversify from equity</span>
                </li>
                <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">High-income taxpayers (for tax-free bonds)</span>
                </li>
                   <li className="flex items-start">
                  <span className="text-xl mr-2">✓</span>
                  <span className="text-gray-700">Retired individuals seeking regular income</span>
                </li>
              </ul>
            </section>

            {/* CTA */}
            <section className="text-center bg-purple-600 text-white rounded-2xl p-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Start Investing in Bonds Today
              </h2>
              <p className="text-purple-100 mb-6">
                Get expert guidance on choosing the right bonds for your financial goals
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/#contact"
                  className="inline-block bg-white text-purple-700 px-8 py-4 rounded-xl hover:bg-gray-50 transition-all shadow-lg font-semibold"
                >
                  Request Consultation
                </Link>
                <a
                  href="https://wa.me/919248852187?text=Hi, I want to know more about Bond investments"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-green-500 text-white px-8 py-4 rounded-xl hover:bg-green-600 transition-all shadow-lg font-semibold"
                >
                  WhatsApp Now
                </a>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
